import React, { createContext, useState, useContext, useEffect } from 'react';
import { GetPassPolicies } from '../services/pass-service';
import { PassPolicy } from 'models/IPassPolicy';
import { useUserContext } from './user';

interface PassPoliciesContextProps {
  passPolicies: PassPolicy[];
  setPassPolicies: React.Dispatch<React.SetStateAction<PassPolicy[]>>;
  clearContext: () => void;
  isInitialLoading: boolean;
  policyLoadError: Error | null;
}

const PassPoliciesContext = createContext<PassPoliciesContextProps | undefined>(undefined);

export const PassPoliciesProvider: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const [passPolicies, setPassPolicies] = useState<PassPolicy[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [policyLoadError, setPolicyLoadError] = useState<Error | null>(null);
  const userInfo = useUserContext();

  useEffect(() => {
    fetchPolicies();
  }, [userInfo?.passplayUser?.sponsorId]);

  const fetchPolicies = () => {
    const sponsorId = userInfo?.passplayUser?.sponsorId;
    if (sponsorId) {
      setPolicyLoadError(null);
      GetPassPolicies(sponsorId)
        .then(policies => {
          setPassPolicies(policies.data || []);
        })
        .catch(err => {
          setPolicyLoadError(err);
          setPassPolicies([]);
        })
        .finally(() => {
          setIsInitialLoading(false);
        });
    }
  };

  const clearContext = () => {
    fetchPolicies();
  };

  return (
    <PassPoliciesContext.Provider
      value={{ passPolicies, setPassPolicies, clearContext, isInitialLoading, policyLoadError }}
    >
      {children}
    </PassPoliciesContext.Provider>
  );
};

export const usePassPolicies = () => {
  const context = useContext(PassPoliciesContext);
  if (context === undefined) {
    throw new Error('usePassPolicies must be used within a PassPoliciesProvider');
  }
  return context;
};
